/* eslint-disable no-useless-rename */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, useContext, useState } from "react";
import axios from "axios";
import { api } from "../../lib/api";

import { usePartnerData } from "../partnerData";

import { useAbortableRequest } from "../../hooks/abortableController";
import { IFilters } from "../filters/types";
import {
  IDoctorData,
  IDoctorsDataProps,
  IDoctorsDataProvider,
  IDoctorSpecialty,
} from "./types";
import {
  buildListCacheKey,
  IListCachePayload,
  readListCache,
  writeListCache,
} from "../../utils/listCache";

export const DoctorsDataContext = createContext<IDoctorsDataProvider>(
  {} as IDoctorsDataProvider
);

export const DoctorsDataProvider = ({ children }: IDoctorsDataProps) => {
  const [doctors, setDoctors] = useState<IDoctorData[]>([]);
  const [searching, setSearching] = useState<boolean>(true);
  const [revalidating, setRevalidating] = useState<boolean>(false);
  const [doctorsCount, setDoctorsCount] = useState("");
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [allInsurances, setAllInsurances] = useState<IDoctorSpecialty[]>([]);
  const [allSpecs, setAllSpecs] = useState<IDoctorSpecialty[]>([]);
  const [insurances, setInsurances] = useState<IDoctorSpecialty[]>([]);
  const [specs, setSpecs] = useState<IDoctorSpecialty[]>([]);

  const { partnerData, hasPartner } = usePartnerData();
  const { createController } = useAbortableRequest();

  const applyListData = (payload: IListCachePayload) => {
    setDoctors(payload.doctors);
    setSpecs(payload.specs);
    setInsurances(payload.insurances);
    setDoctorsCount(payload.doctorsCount);
    setPages(payload.pages);
    setPage(payload.page);
  };

  const getDoctorsWithFilter = async (params: IFilters) => {
    const abortController = createController(`getDoctorsWithFilter`);
    if (!hasPartner || !partnerData?.idEmpresa) {
      return;
    }

    if (partnerData?.idEmpresa) {
      params.idPartner = partnerData?.idEmpresa;
    }

    const cacheKey = buildListCacheKey(params);
    const cached = readListCache(cacheKey);

    if (cached) {
      // SWR: pinta a lista do cache imediatamente e revalida por trás
      applyListData(cached);
      if (allSpecs.length <= 0) {
        setAllSpecs(cached.specs);
        setAllInsurances(cached.insurances);
      }
      setSearching(false);
      setRevalidating(true);
    } else {
      setSearching(true);
    }

    try {
      const size = 20;

      if (params.date !== "") {
        params.date = new Date(params.date).toISOString().split("T")[0];
      }

      const response = await api.get(`/SpecialistDoctor/list`, {
        params: {
          ...params,
          size,
        },
        signal: abortController.signal,
      });

      const data = response.data;
      const link = response.headers.link || response.headers.Link;
      const count =
        response.headers["x-total-count"] ||
        response.headers["X-Total-Count"] ||
        response.headers["total-count"] ||
        response.headers["Total-Count"];

      let newPages = pages;
      let newPage = page;
      if (link) {
        newPages = Number(link.split(",")[2].split("page=")[1].split(">;")[0]);
        newPage = Number(link.split(",")[0].split("page=")[1].split(">;")[0]);
      }

      const newCount =
        count ?? String(data.specialistDoctors?.length ?? 0);

      const payload: IListCachePayload = {
        doctors: data.specialistDoctors,
        specs: data.specs,
        insurances: data.insurance,
        doctorsCount: newCount,
        pages: newPages,
        page: newPage,
      };

      applyListData(payload);
      writeListCache(cacheKey, payload);

      if (allSpecs.length <= 0) {
        setAllSpecs(data.specs);
        setAllInsurances(data.insurance);
      }

      setSearching(false);
      setRevalidating(false);
    } catch (error) {
      if (axios.isCancel(error)) return; // chamada superada; a nova é dona das flags
      console.error(error);
      // requisição abortada/falha: não deixa flags presas
      setRevalidating(false);
      if (!cached) {
        setSearching(false);
      }
    }
  };

  const getData = async () => {
    if (!hasPartner || !partnerData?.idEmpresa) {
      return;
    }

    const abortController = createController(`getData`);
    try {
      // setIsLoading(true);
      const { data } = await api.get(`/SpecialistDoctor/list`, {
        params: {
          idPartner: partnerData.idEmpresa
        },
        signal: abortController.signal
      });

      setAllSpecs(data.specs);
      setAllInsurances(data.insurance);
      // setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <DoctorsDataContext.Provider
      value={{
        getData,
        setPage,
        page,
        pages,
        specs,
        insurances,
        allSpecs,
        allInsurances,
        doctorsCount,
        doctors,
        searching,
        revalidating,
        getDoctorsWithFilter,
      }}
    >
      {children}
    </DoctorsDataContext.Provider>
  );
};

export const useDoctorsData = () => useContext(DoctorsDataContext);
